import {
  Box, Button, ListItemIcon, Menu, MenuItem
} from '@mui/material'
import { MouseEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useCorporationSettings from '../../../../hooks/useCorporationSettings'
import Dropbox from '../../../cloudUpload/providers/dropbox'
import GoogleDrive from '../../../cloudUpload/providers/google-drive'
import OneDrivePicker from '../../../cloudUpload/providers/one-drive'

// New design (redesign round 1): one row of small buttons under a question instead of the
// "Kommentare" / "Anhänge" accordions and the separate photo, document, cloud and task buttons.
// Every button calls what the old buttons called; the dialogs and lists stay the same.

interface Props {
  commentCount: number
  attachmentCount: number
  taskCount: number
  showComments: boolean
  showAttachments: boolean
  showCloud: boolean
  showTasks: boolean
  disabled: boolean
  tasksDisabled: boolean
  commentsOpen: boolean
  onComments: () => void
  onPhoto: () => void
  onDocument: () => void
  // files chosen in a provider's picker (the old AttachmentPicker passed them the same way)
  onCloudSelect: (files: any, accessToken?: string | null) => void
  onTasks: () => void
}

const buttonSx = (active: boolean) => ({
  height: 32,
  px: 1.5,
  borderRadius: '8px',
  border: '1px solid',
  borderColor: active ? '#0063C4' : '#C3CCD8',
  color: active ? '#0050A0' : '#142033',
  bgcolor: '#FFFFFF',
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 600,
  gap: 0.75,
  minWidth: 0,
  '&:hover': { bgcolor: '#F4F6F9' }
}) as const

export default function QuestionActions(props: Props) {
  const {
    commentCount, attachmentCount, taskCount, showComments, showAttachments, showCloud, showTasks,
    disabled, tasksDisabled, commentsOpen, onComments, onPhoto, onDocument, onCloudSelect, onTasks
  } = props
  const { t } = useTranslation()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [cloudOpen, setCloudOpen] = useState(false)
  const attachButton = useRef<HTMLButtonElement | null>(null)
  const { use_onedrive, use_google_drive, use_dropbox } = useCorporationSettings()
  const hasProviders = !!(use_google_drive || use_dropbox || use_onedrive)
  const closeCloud = () => setCloudOpen(false)

  if (!showComments && !showAttachments && !showCloud && !showTasks) return null

  const label = (text: string, count: number) => (count > 0 ? `${text} ${count}` : text)

  return (
    <Box className="nd-question-actions" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
      {showTasks && (
        <Button onClick={onTasks} disabled={tasksDisabled} sx={buttonSx(taskCount > 0)}>
          <i className="fas fa-tasks" style={{ fontSize: 12 }} aria-hidden />
          {label(t('questionActions.task'), taskCount)}
        </Button>
      )}
      {(showAttachments || showCloud) && (
        <>
          <Button
            ref={attachButton}
            onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
            disabled={disabled}
            aria-haspopup="menu"
            sx={buttonSx(attachmentCount > 0)}
          >
            <i className="fas fa-paperclip" style={{ fontSize: 12 }} aria-hidden />
            {label(t('questionActions.attachment'), attachmentCount)}
            <i className="fas fa-angle-down" style={{ fontSize: 11 }} aria-hidden />
          </Button>
          <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
            {showAttachments && (
              <MenuItem onClick={() => { setAnchor(null); onPhoto() }} sx={{ fontSize: 14, minHeight: 40 }}>
                <ListItemIcon sx={{ minWidth: 30 }}><i className="fas fa-camera" /></ListItemIcon>
                {t('questionActions.photo')}
              </MenuItem>
            )}
            {showAttachments && (
              <MenuItem onClick={() => { setAnchor(null); onDocument() }} sx={{ fontSize: 14, minHeight: 40 }}>
                <ListItemIcon sx={{ minWidth: 30 }}><i className="fas fa-paperclip" /></ListItemIcon>
                {t('questionActions.document')}
              </MenuItem>
            )}
            {showCloud && hasProviders && (
              <MenuItem onClick={() => { setAnchor(null); setCloudOpen(true) }} sx={{ fontSize: 14, minHeight: 40 }}>
                <ListItemIcon sx={{ minWidth: 30 }}><i className="fas fa-cloud" /></ListItemIcon>
                {t('questionActions.cloud')}
                <i className="fas fa-angle-right" style={{ marginLeft: 'auto', paddingLeft: 16, fontSize: 12 }} aria-hidden />
              </MenuItem>
            )}
          </Menu>
          {/* providers: kept mounted, their pickers report the chosen files back asynchronously */}
          {showCloud && hasProviders && (
            <Menu
              anchorEl={attachButton.current}
              open={cloudOpen}
              onClose={closeCloud}
              keepMounted
              className="nd-cloud-providers"
            >
              <MenuItem onClick={() => { closeCloud(); setAnchor(attachButton.current) }} sx={{ fontSize: 13, color: '#4B5565', minHeight: 36 }}>
                <i className="fas fa-angle-left" style={{ marginRight: 10, fontSize: 12 }} aria-hidden />
                {t('questionActions.cloud')}
              </MenuItem>
              {use_google_drive && (
                <MenuItem disableRipple sx={{ minHeight: 40 }}>
                  <GoogleDrive onSelect={onCloudSelect} onClick={closeCloud} />
                </MenuItem>
              )}
              {use_dropbox && (
                <MenuItem disableRipple sx={{ minHeight: 40 }}>
                  <Dropbox onSelect={onCloudSelect} onClick={closeCloud} />
                </MenuItem>
              )}
              {use_onedrive && (
                <MenuItem disableRipple sx={{ minHeight: 40 }}>
                  <OneDrivePicker onSelect={onCloudSelect as any} onClick={closeCloud} />
                </MenuItem>
              )}
            </Menu>
          )}
        </>
      )}
      {showComments && (
        <Button onClick={onComments} sx={buttonSx(commentsOpen || commentCount > 0)} aria-expanded={commentsOpen}>
          <i className="far fa-comment" style={{ fontSize: 12 }} aria-hidden />
          {label(t('questionActions.comment'), commentCount)}
        </Button>
      )}
    </Box>
  )
}
